import { Head, Link } from "@inertiajs/react"
import { format } from "date-fns"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  ArrowLeft,
  Edit,
  Trash2,
  Calendar,
  DollarSign,
  FileText,
} from "lucide-react"
import { AppLayout } from "@/layouts/app-layout"
import { expensesPath, editExpensePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Expenses",
    href: expensesPath(),
  },
  {
    title: "Expense Details",
    href: "#",
  },
]

interface ExpenseShowProps {
  expense: {
    id: number
    amount: number
    category: string
    description: string | null
    expense_date: string
    created_at: string
    updated_at: string
  }
}

export default function ExpenseShow({ expense }: ExpenseShowProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMMM dd, yyyy")
  }

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), "MMM dd, yyyy 'at' h:mm a")
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Expense Details" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link href={expensesPath()}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Expenses
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Expense Details</h1>
              <p className="text-muted-foreground">
                View and manage this expense
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild>
              <Link href={editExpensePath(expense.id)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit Expense
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (
                  confirm(
                    "Are you sure you want to delete this expense? This action cannot be undone.",
                  )
                ) {
                  // Handle delete - this would typically use Inertia's delete method
                  window.location.href = expensesPath()
                }
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>

        {/* Expense Details */}
        <div className="grid gap-6 md:grid-cols-3">
          {/* Main Details */}
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Expense Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Amount */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="text-muted-foreground h-5 w-5" />
                    <span className="text-muted-foreground text-sm font-medium">
                      Amount
                    </span>
                  </div>
                  <span className="text-2xl font-bold">
                    {formatCurrency(expense.amount)}
                  </span>
                </div>

                {/* Category */}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm font-medium">
                    Category
                  </span>
                  <Badge variant="outline">{expense.category}</Badge>
                </div>

                {/* Description */}
                <div>
                  <div className="text-muted-foreground mb-2 text-sm font-medium">
                    Description
                  </div>
                  <div className="text-sm">
                    {expense.description ?? (
                      <span className="text-muted-foreground italic">
                        No description provided
                      </span>
                    )}
                  </div>
                </div>

                {/* Expense Date */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="text-muted-foreground h-4 w-4" />
                    <span className="text-muted-foreground text-sm font-medium">
                      Expense Date
                    </span>
                  </div>
                  <span className="text-sm">
                    {formatDate(expense.expense_date)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Metadata */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-muted-foreground mb-1 text-sm font-medium">
                    Created
                  </div>
                  <div className="text-sm">
                    {formatDateTime(expense.created_at)}
                  </div>
                </div>

                <div>
                  <div className="text-muted-foreground mb-1 text-sm font-medium">
                    Last Updated
                  </div>
                  <div className="text-sm">
                    {formatDateTime(expense.updated_at)}
                  </div>
                </div>

                <div>
                  <div className="text-muted-foreground mb-1 text-sm font-medium">
                    Expense ID
                  </div>
                  <div className="font-mono text-sm">#{expense.id}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
